import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, TextField, Button, Divider,
  CircularProgress, Alert, IconButton, Table, TableHead,
  TableRow, TableCell, TableBody, Tooltip, MenuItem, Select,
  FormControl, InputLabel, Chip, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api/setting/mercari-routing-settings`;
const ORDER_BY_OPTIONS_URL = `${BACKEND_URL}/api/setting/order-by-options`;

const DEFAULT_TIERS = [
  { maxUrgency: 0.20, merpayMaxPct: 100 },
  { maxUrgency: 0.35, merpayMaxPct: 86 },
  { maxUrgency: 0.50, merpayMaxPct: 64 },
  { maxUrgency: 0.65, merpayMaxPct: 50 },
  { maxUrgency: 1.00, merpayMaxPct: 36 },
];

export default function MercariRoutingSettingsSetting() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mercariAccounts, setMercariAccounts] = useState([]);

  const [togglingFundId, setTogglingFundId] = useState(null);

  // Carry-over balances (per mercari account, editable)
  const [carryOverBalances, setCarryOverBalances] = useState({}); // { id: value }
  const [savingBalance, setSavingBalance] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const [form, setForm] = useState({
    sevenElevenResetHourThai: 17,
    auctionUnlockHourThai: 20,
    urgencyWindowStartThai: 7,
    urgencyWindowEndThai: 22,
    ordersPerAccountBeforeRotate: 4,
    merpayBaseMaxJpy: 70000,
    sevenMinAmountJpy: 35000,
    urgencyWeightMerpayDepletion: 0.60,
    urgencyWeightTime: 0.40,
    urgencyTimeExponent: 3.5,
    urgencyTiers: DEFAULT_TIERS,
    routingSuggestionVisible: false,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, optionsRes] = await Promise.all([
        axios.get(API_URL),
        axios.get(ORDER_BY_OPTIONS_URL),
      ]);
      const s = settingsRes.data;
      setForm({
        sevenElevenResetHourThai: s.sevenElevenResetHourThai ?? 17,
        auctionUnlockHourThai: s.auctionUnlockHourThai ?? 20,
        urgencyWindowStartThai: s.urgencyWindowStartThai ?? 7,
        urgencyWindowEndThai: s.urgencyWindowEndThai ?? 22,
        ordersPerAccountBeforeRotate: s.ordersPerAccountBeforeRotate ?? 4,
        merpayBaseMaxJpy: s.merpayBaseMaxJpy ?? 70000,
        sevenMinAmountJpy: s.sevenMinAmountJpy ?? 35000,
        urgencyWeightMerpayDepletion: s.urgencyWeightMerpayDepletion ?? 0.60,
        urgencyWeightTime: s.urgencyWeightTime ?? 0.40,
        urgencyTimeExponent: s.urgencyTimeExponent ?? 3.5,
        urgencyTiers: s.urgencyTiers ?? DEFAULT_TIERS,
        routingSuggestionVisible: s.routingSuggestionVisible ?? false,
      });
      const accounts = (optionsRes.data || []).filter(o => o.orderByType === 'mercariAccount');
      setMercariAccounts(accounts);
      // Seed carry-over balance state from each account
      const balanceMap = {};
      accounts.forEach(a => { balanceMap[a.id] = a.carryOverBalanceJpy ?? 0; });
      setCarryOverBalances(balanceMap);
    } catch (err) {
      setError('ไม่สามารถโหลดการตั้งค่าได้');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNumChange = (field) => (e) => {
    const v = e.target.value;
    handleChange(field, v === '' ? '' : Number(v));
  };

  // ── Urgency tiers ──────────────────────────────────────────────────────────
  const handleTierChange = (idx, field, value) => {
    setForm(prev => {
      const tiers = [...prev.urgencyTiers];
      tiers[idx] = { ...tiers[idx], [field]: value === '' ? '' : Number(value) };
      return { ...prev, urgencyTiers: tiers };
    });
  };

  const addTier = () => {
    setForm(prev => ({
      ...prev,
      urgencyTiers: [...prev.urgencyTiers, { maxUrgency: 1.00, merpayMaxPct: 36 }],
    }));
  };

  const removeTier = (idx) => {
    setForm(prev => ({
      ...prev,
      urgencyTiers: prev.urgencyTiers.filter((_, i) => i !== idx),
    }));
  };

  // ── Tomorrow Fund (per Mercari ID) ────────────────────────────────────────
  const handleToggleTomorrowFundForAccount = async (accountId, enable) => {
    try {
      setTogglingFundId(accountId);
      await axios.post(`${ORDER_BY_OPTIONS_URL}/${accountId}/toggle-tomorrow-fund`, { enable });
      setMercariAccounts(prev =>
        prev.map(a =>
          a.id === accountId
            ? { ...a, tomorrowFundEnabled: enable, tomorrowFundEnabledAt: enable ? new Date().toISOString() : null }
            : a
        )
      );
      toast.success(
        enable
          ? 'เปิดใช้งบพรุ่งนี้ใน urgency สำหรับ ID นี้แล้ว (จะปิดอัตโนมัติหลัง 02:00 น. ไทย)'
          : 'ปิด Tomorrow Fund สำหรับ ID นี้แล้ว'
      );
    } catch (err) {
      const msg = err?.response?.data?.message || 'เปลี่ยน Tomorrow Fund ไม่สำเร็จ';
      toast.error(msg);
    } finally {
      setTogglingFundId(null);
    }
  };

  // ── Carry-over balance ────────────────────────────────────────────────────
  const handleCarryOverChange = (id, value) => {
    setCarryOverBalances(prev => ({ ...prev, [id]: value === '' ? '' : Number(value) }));
  };

  const handleSaveCarryOver = async () => {
    try {
      setSavingBalance(true);
      await Promise.all(
        mercariAccounts.map(a =>
          axios.put(`${ORDER_BY_OPTIONS_URL}/${a.id}`, { carryOverBalanceJpy: carryOverBalances[a.id] ?? 0 })
        )
      );
      toast.success('บันทึก carry-over balance เรียบร้อย');
    } catch {
      toast.error('บันทึก balance ไม่สำเร็จ');
    } finally {
      setSavingBalance(false);
    }
  };

  const handleRecalculateBalance = async () => {
    try {
      setRecalculating(true);
      const res = await axios.post(`${API_URL}/recalculate-balance`);
      const results = res.data?.updated ?? [];
      // Update local state from result
      const newMap = {};
      results.forEach(r => { newMap[r.id] = r.newCarryOver; });
      setCarryOverBalances(prev => ({ ...prev, ...newMap }));
      toast.success(`คำนวณ carry-over เรียบร้อย — อัปเดต ${results.length} account`);
    } catch {
      toast.error('คำนวณ balance ไม่สำเร็จ');
    } finally {
      setRecalculating(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Validate weight sum (only 2 weights now)
    const totalWeight = (
      Number(form.urgencyWeightMerpayDepletion) +
      Number(form.urgencyWeightTime)
    );
    if (Math.abs(totalWeight - 1) > 0.01) {
      toast.warning(`น้ำหนัก urgency รวมกันต้องเท่ากับ 1.00 (ปัจจุบัน: ${totalWeight.toFixed(2)})`);
      return;
    }

    try {
      setSaving(true);
      await axios.put(API_URL, {
        ...form,
        urgencyTimeExponent: Number(form.urgencyTimeExponent),
        urgencyTiers: form.urgencyTiers.map(t => ({
          maxUrgency: Number(t.maxUrgency),
          merpayMaxPct: Number(t.merpayMaxPct),
        })),
      });
      toast.success('บันทึกการตั้งค่าเรียบร้อย');
      fetchData();
    } catch (err) {
      toast.error('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const totalWeight = (
    Number(form.urgencyWeightMerpayDepletion) +
    Number(form.urgencyWeightTime)
  );
  const weightOk = Math.abs(totalWeight - 1) <= 0.01;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        ⚙️ ตั้งค่าระบบ Routing Mercari
      </Typography>

      {/* ── Suggestion banner visibility ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}>Routing Suggestion Banner</Typography>
        <Alert severity={form.routingSuggestionVisible ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {form.routingSuggestionVisible
            ? 'แสดง banner แนะนำใน OrderForm — admin เห็นคำแนะนำแล้ว'
            : 'ซ่อน banner — ระบบยังคำนวณและบันทึกคำแนะนำไว้ในฐานข้อมูลเพื่อดูใน Report'}
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Switch
            checked={!!form.routingSuggestionVisible}
            onChange={(e) => handleChange('routingSuggestionVisible', e.target.checked)}
          />
          <Typography variant="body2">
            {form.routingSuggestionVisible ? 'แสดง banner (เปิด)' : 'ซ่อน banner (ปิด — แนะนำสำหรับช่วง shadow mode)'}
          </Typography>
        </Box>
      </Paper>

      {/* ── 7-Eleven ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>7-Eleven</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="เวลา Reset 7-Eleven (โมงไทย)"
              value={form.sevenElevenResetHourThai} onChange={handleNumChange('sevenElevenResetHourThai')}
              inputProps={{ min: 0, max: 23 }}
              helperText="สล็อต 7-Eleven จะรีเซ็ตเวลานี้ทุกวัน (ค่าปัจจุบัน = 17:00)"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Auction ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>สินค้า Auction</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          กำหนด Auction Account ได้ต่อ Mercari ID ใน <strong>Order By Options</strong> (เปิด isAuctionAccount + ระบุ shop)
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="เวลา Unlock ลิมิต Auction (โมงไทย)"
              value={form.auctionUnlockHourThai} onChange={handleNumChange('auctionUnlockHourThai')}
              inputProps={{ min: 0, max: 23 }}
              helperText="หลังเวลานี้ขยาย dailyAmountLimit ของ Auction Account เป็น Extended Limit"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Eve & Round-robin ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>Urgency Window & Round-Robin</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Urgency เริ่มนับ (โมงไทย)"
              value={form.urgencyWindowStartThai} onChange={handleNumChange('urgencyWindowStartThai')}
              inputProps={{ min: 0, max: 23 }}
              helperText="ช่วงต้น window — urgency ยังต่ำ" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Urgency สูงสุด (โมงไทย)"
              value={form.urgencyWindowEndThai} onChange={handleNumChange('urgencyWindowEndThai')}
              inputProps={{ min: 0, max: 23 }}
              helperText="ถึงเวลานี้ time factor = 1.0 (สูงสุด) · หลังเวลานี้ Mercari5 ได้รับ Extended Cap" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="จำนวน Order ก่อน Rotate (ต่อลูกค้า)"
              value={form.ordersPerAccountBeforeRotate} onChange={handleNumChange('ordersPerAccountBeforeRotate')}
              inputProps={{ min: 1, max: 20 }}
              helperText="ลูกค้า 1 คนส่ง Order ให้ Mercari ID เดิมกี่ครั้งก่อนสลับ" />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Merpay Urgency ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}>Merpay Urgency</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          ระบบคำนวณ urgency score จาก 2 ปัจจัย แล้วใช้ Tier ด้านล่างเพื่อกำหนดเพดานราคาสูงสุดที่ Merpay รับได้
        </Typography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Max ปกติ ¥ (merpayBaseMaxJpy)"
              type="number" value={form.merpayBaseMaxJpy}
              onChange={handleNumChange('merpayBaseMaxJpy')}
              helperText="100% tier = ค่านี้ Tier อื่น = % ของค่านี้" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="เพดานแนะนำ 7-Eleven ¥ (sevenMinAmountJpy)"
              type="number" value={form.sevenMinAmountJpy}
              onChange={handleNumChange('sevenMinAmountJpy')}
              helperText="Order ที่ ≥ ค่านี้ ระบบแนะนำให้จ่ายผ่าน 7-Eleven แทน Merpay" />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" mb={1}>น้ำหนัก Urgency Factors (รวมต้องเท่ากับ 1.00)</Typography>
        <Grid container spacing={2} mb={1}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="น้ำหนัก: Merpay เหลือน้อย"
              type="number" value={form.urgencyWeightMerpayDepletion}
              onChange={handleNumChange('urgencyWeightMerpayDepletion')}
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              helperText="ยิ่ง Merpay เหลือน้อย urgency ยิ่งสูง" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="น้ำหนัก: เวลา (Eve window)"
              type="number" value={form.urgencyWeightTime}
              onChange={handleNumChange('urgencyWeightTime')}
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              helperText="ยิ่งใกล้ Eve หยุด urgency ยิ่งสูง (shape กำหนดด้วย Exponent)" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Time Exponent (curve shape)"
              type="number" value={form.urgencyTimeExponent}
              onChange={handleNumChange('urgencyTimeExponent')}
              inputProps={{ step: 0.5, min: 0.5, max: 10 }}
              helperText="1=linear | 3.5=spike ช่วงท้าย | 6+=spike ชั่วโมงสุดท้าย" />
          </Grid>
        </Grid>
        <Alert severity={weightOk ? 'success' : 'warning'} sx={{ mb: 2 }}>
          รวม: {totalWeight.toFixed(2)} {weightOk ? '✓' : '— ต้องเท่ากับ 1.00'}
        </Alert>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Urgency Tiers — เพดาน Merpay ต่อ Order</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addTier}>เพิ่ม Tier</Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Score สูงสุด (0–1)</TableCell>
              <TableCell>% ของ Base Max</TableCell>
              <TableCell>≈ ¥ ต่อ Order</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {form.urgencyTiers.map((tier, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField size="small" type="number" value={tier.maxUrgency}
                    onChange={(e) => handleTierChange(idx, 'maxUrgency', e.target.value)}
                    inputProps={{ step: 0.05, min: 0, max: 1 }} sx={{ width: 100 }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={tier.merpayMaxPct}
                    onChange={(e) => handleTierChange(idx, 'merpayMaxPct', e.target.value)}
                    inputProps={{ step: 1, min: 0, max: 100 }}
                    InputProps={{ endAdornment: '%' }} sx={{ width: 100 }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    ¥{Math.round(form.merpayBaseMaxJpy * tier.merpayMaxPct / 100).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="ลบ Tier นี้">
                    <IconButton size="small" color="error"
                      onClick={() => removeTier(idx)}
                      disabled={form.urgencyTiers.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          Tier สุดท้ายควรมี Score สูงสุด = 1.00 เพื่อรองรับทุกกรณี
        </Typography>
      </Paper>

      {/* ── Merpay Carry-Over + Tomorrow Fund (per ID) ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Merpay Carry-Over & Tomorrow Fund</Typography>
          <Tooltip title="คำนวณ carry-over จากเมื่อวาน (จำนวน Merpay ที่เหลือ)">
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRecalculateBalance}
              disabled={recalculating}
              sx={{ mr: 1 }}
            >
              {recalculating ? 'กำลังคำนวณ...' : 'คำนวณจากเมื่อวาน'}
            </Button>
          </Tooltip>
          <Button size="small" variant="contained" startIcon={<SaveIcon />}
            onClick={handleSaveCarryOver} disabled={savingBalance}>
            {savingBalance ? 'บันทึก...' : 'บันทึก Balance'}
          </Button>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="span">
            <strong>Tomorrow Fund (ต่อ Mercari ID)</strong> — เปิด<strong>ครั้งเดียว</strong>เมื่อทีมญี่ปุ่นแจ้งว่า Merpay เติมแล้ว
            เพื่อให้ระบบนับ cap ของ ID นั้น<strong>ซ้ำหนึ่งวัน</strong>ในการคำนวณ urgency <strong>ช่วงวันบริการปัจจุบันเท่านั้น</strong>
            (ไม่ใช่การตั้งค้างว่า “ทุกวันมีเงินเติมก่อนเวลา”)
          </Typography>
          <Typography variant="body2" component="span" sx={{ display: 'block', mt: 1 }}>
            สวิตช์จะถูกปิดให้อัตโนมัติเมื่อข้ามรอบ <strong>02:00 น. ไทย</strong> (เริ่มวันบริการใหม่ตามที่ตั้งระบบ)
            — วันถัดไปถ้ามีเติมจริงแจ้งมาอีกครั้งค่อยเปิดใหม่
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Carry-over: Merpay ที่เหลือจากเมื่อวาน — หักก่อนคำนวณ urgency
          · เปิด TF ได้หลัง 22:00 ไทย (หรือ 00:00–01:59) ต่อบัญชีที่ต้องการ
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell align="right">Cap ต่อวัน (¥)</TableCell>
              <TableCell align="right">Carry-Over (¥)</TableCell>
              <TableCell align="center">Tomorrow Fund</TableCell>
              <TableCell align="right">Cap ใน urgency (¥)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mercariAccounts.map(a => {
              const cap = a.dailyAmountLimitJpy || 0;
              const tfOn = !!a.tomorrowFundEnabled;
              const effCap = cap + (tfOn ? cap : 0);
              return (
                <TableRow key={a.id}>
                  <TableCell>{a.label}</TableCell>
                  <TableCell align="right">¥{cap.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={carryOverBalances[a.id] ?? 0}
                      onChange={e => handleCarryOverChange(a.id, e.target.value)}
                      inputProps={{ min: 0, step: 1000 }}
                      sx={{ width: 140 }}
                      InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>¥</Typography> }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="ครั้งเดียวต่อรอบวันบริการ — ปิดอัตโนมัติหลัง 02:00 น. ไทย · เปิดได้หลัง 22:00 หรือ 00:00–01:59">
                      <Switch
                        checked={tfOn}
                        disabled={togglingFundId === a.id}
                        onChange={(_, checked) => handleToggleTomorrowFundForAccount(a.id, checked)}
                        color="success"
                      />
                    </Tooltip>
                    {tfOn && (
                      <Chip label="ใช้งบพรุ่งนี้วันนี้" color="success" size="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    ¥{effCap.toLocaleString()}
                    {tfOn && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        cap × 2 ใน urgency จนถึงรอบ 02:00 น. ไทย
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {mercariAccounts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ไม่พบ Mercari Account — กรุณาตั้งค่า orderByType = mercariAccount ใน Order By Options
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" size="large" startIcon={<SaveIcon />}
          onClick={handleSave} disabled={saving || !weightOk}>
          {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
        </Button>
      </Box>
    </Box>
  );
}
